import { fetchDeadlineSnapshot } from "@/services/reality/fetchDeadlineSnapshot";
import type { InscriptionStatusResponse } from "@/services/inscription/types";
import type { RealityInfo } from "@/services/reality/types/reality.types";
import { queryClient } from "@/services/react-query";
import { realityId } from "@/utils/reality-id";
import { useCallback, useEffect, useRef, useState } from "react";

type Params = {
  userId: number | undefined;
  enabled: boolean;
  /** Refetch when route changes (authenticated app). */
  routeKey: string;
};

/**
 * Fresh API snapshot for blocking deadline modals — never reads stale React Query cache.
 */
export function useBlockingDeadlineSnapshot({
  userId,
  enabled,
  routeKey,
}: Params) {
  const [realityInfo, setRealityInfo] = useState<RealityInfo | undefined>();
  const [inscription, setInscription] = useState<
    InscriptionStatusResponse | undefined
  >();
  const [isLoading, setIsLoading] = useState(false);
  const lastFetchAt = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled || !userId) {
      setRealityInfo(undefined);
      setInscription(undefined);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const snapshot = await fetchDeadlineSnapshot();
      setRealityInfo(snapshot.realityInfo);
      setInscription(snapshot.inscription);

      queryClient.setQueryData(["reality-info"], snapshot.realityInfo);
      queryClient.setQueryData(
        ["inscription-status", String(realityId), String(userId)],
        snapshot.inscription
      );
    } finally {
      setIsLoading(false);
    }
  }, [enabled, userId]);

  const refreshThrottled = useCallback(() => {
    const now = Date.now();
    if (now - lastFetchAt.current < 800) return;
    lastFetchAt.current = now;
    void refresh();
  }, [refresh]);

  useEffect(() => {
    void refresh();
  }, [refresh, routeKey]);

  useEffect(() => {
    if (!enabled || !userId) return;

    const onVisible = () => {
      if (document.visibilityState === "visible") refreshThrottled();
    };
    const onFocus = () => refreshThrottled();

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [enabled, userId, refreshThrottled]);

  return { realityInfo, inscription, isLoading, refresh };
}
